import { FC, useEffect, useRef, useState } from "react";
import { App, URLOpenListenerEvent } from "@capacitor/app";
import { useHistory } from "react-router";
import { useSelector } from "react-redux";
import { appLaunchUrlToPath } from "../utils/AppUrlHelper";
import { useUserLoggedIn } from "./hooks";
import { getUTMParamsFromURL } from "../services/analytics/utmAttribution";
import { mpRegisterSuperProperties } from "../services/analytics/mixpanelService";
import { trackEvent } from "../services/analytics/analyticsService";
import { routes } from "../route/constants";
import { isAndroid, isIos } from "../services/platformDetection";
import { verifyMagicLink, MagicLinkVerificationResponse } from "../services/authService";
import { LoginSession } from "../redux/models/loginSession";
import { completeLogin } from "../services/loginService";
import { setPendingMagicLinkAlert } from "../redux/actions/appStatus";
import { store } from "../redux/store";
import { processIosGarminDevices } from "../services/watch/GarminWatchHelper";
import { useTranslation } from "react-i18next";
import AlertComponent from "./AlertComponent";
import { RootState } from "../redux/store";

const magicLinkVerificationCache = new Map<
    string,
    Promise<MagicLinkVerificationResponse>
>();

export const isExpiredMagicLinkMessage = (message?: string) => {
    if (!message) {
        return false;
    }
    const normalizedMessage = message.toLowerCase();
    return (
        normalizedMessage.includes("expired") ||
        normalizedMessage.includes("already been used")
    );
};

/** Deep linking code for Android and iOS */
const AppUrlListener: FC<any> = () => {
    let history = useHistory();
    const loggedIn = useUserLoggedIn();
    const currentUserId = useSelector((state: RootState) => state.auth.session?.user_id);
    const { t } = useTranslation();
    const [showExpiredMagicLinkAlert, setShowExpiredMagicLinkAlert] = useState(false);
    const listenerHandleRef = useRef<{ remove: () => Promise<void> | void } | null>(null);
    const loggedInRef = useRef(loggedIn);
    const currentUserIdRef = useRef(currentUserId);

    console.log("AppUrlListener called");
    useEffect(() => {
        loggedInRef.current = loggedIn;
        currentUserIdRef.current = currentUserId;
    }, [loggedIn, currentUserId]);

    useEffect(() => {
        const handleExpiredMagicLink = () => {
            if (!loggedInRef.current) {
                // Not logged in - show expired alert so they can request a new link
                setShowExpiredMagicLinkAlert(true);
            } else {
                // Already logged in - redirect to app, no need to show error
                // They're already authenticated so the expired link doesn't matter
                console.log("Magic link expired but user is already logged in, redirecting to app");
                if (window.location.pathname.startsWith("/auth/verify-magic/")) {
                    history.replace(routes.tabResults);
                }
            }
        };

        const handleMagicLinkResult = async (data: MagicLinkVerificationResponse) => {
            if (data.success) {
                // Only skip login if already logged in as the SAME user
                // This prevents consuming a different user's magic link without switching accounts
                const isSameUser = loggedInRef.current &&
                    currentUserIdRef.current &&
                    currentUserIdRef.current === data.user_id;

                if (isSameUser) {
                    console.log("Magic link: already logged in as same user, skipping dispatch");
                    // Navigate away from magic link URL if still there
                    if (window.location.pathname.startsWith("/auth/verify-magic/")) {
                        console.log("Magic link: redirecting to app");
                        history.replace(routes.tabResults);
                    }
                    return;
                }

                // If logged in as a different user, set pending alert and redirect
                // The alert will be shown by MagicLinkAlertHandler on the actual page
                if (loggedInRef.current && currentUserIdRef.current !== data.user_id) {
                    console.log("Magic link: already logged in as different user, setting pending alert");
                    store.dispatch(setPendingMagicLinkAlert("different-user"));
                    history.replace(routes.tabResults);
                    return;
                }

                const session: LoginSession = {
                    issued: data.issued || Date.now(),
                    expires: data.expires || Date.now() + (24 * 60 * 60 * 1000),
                    provider: "magic-link",
                    ip: "",
                    token: data.token || "",
                    password: data.password || "",
                    user_id: data.user_id || "",
                    roles: data.roles || ["user"],
                    userDBs: data.userDBs || { scorza: "" }
                };

                await completeLogin({
                    session,
                    email: data.email || "",
                    skipProfileDownload: true, // Profile will be downloaded when app loads
                });

                console.log("Magic link verification successful");
                // Navigate to main app - OnBoardingManager will handle redirect
                // to onboarding if needed based on userActionsCheckList.onboardingCompleted
                history.replace(routes.tabResults);
            } else {
                console.error("Magic link verification failed:", data.message);
                if (isExpiredMagicLinkMessage(data.message)) {
                    handleExpiredMagicLink();
                }
            }
        };

        const getMagicLinkVerification = (
            token: string,
            source: "web" | "native"
        ): Promise<MagicLinkVerificationResponse> => {
            const cachedResponse = magicLinkVerificationCache.get(token);

            if (cachedResponse) {
                console.log("Magic link token already processed, returning cached result", {
                    token,
                    source,
                });
                return cachedResponse;
            }

            console.log(`Verifying magic link token (${source}):`, token);

            const requestPromise = verifyMagicLink(token)
                .then((response) => response.data as MagicLinkVerificationResponse)
                .catch((error) => {
                    const errorResponse = (error as any)?.response?.data as
                        | MagicLinkVerificationResponse
                        | undefined;

                    if (errorResponse && isExpiredMagicLinkMessage(errorResponse.message)) {
                        return errorResponse;
                    }

                    magicLinkVerificationCache.delete(token);
                    throw error;
                });

            magicLinkVerificationCache.set(token, requestPromise);

            return requestPromise;
        };

        const verifyMagicLinkOnce = async (
            token: string,
            options: { source: "web" | "native"; onNonExpiredError?: () => void }
        ) => {
            try {
                const verificationResult = await getMagicLinkVerification(token, options.source);
                await handleMagicLinkResult(verificationResult);
            } catch (error) {
                console.error(
                    `Error verifying magic link token (${options.source}):`,
                    error
                );
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const errorMessage = (error as any)?.response?.data?.message;

                if (isExpiredMagicLinkMessage(errorMessage)) {
                    handleExpiredMagicLink();
                } else if (options.onNonExpiredError && !loggedInRef.current) {
                    // Only redirect to login if not already logged in
                    options.onNonExpiredError();
                }
            }
        };
        // If running on web, handle deep link from window.location
        const handleWebMagicLink = async () => {
            if (!isAndroid() && !isIos()) {
                const currentUrl = window.location.pathname;
                console.log("Current URL:", currentUrl);
                if (currentUrl && currentUrl.startsWith("/auth/verify-magic/")) {
                    const token = currentUrl.split("/auth/verify-magic/")[1];
                    if (token) {
                        await verifyMagicLinkOnce(token, {
                            source: "web",
                            onNonExpiredError: () => {
                                history.push(routes.login);
                            },
                        });
                    }
                }
            }
        };

        handleWebMagicLink();
        const registerListener = async () => {
            listenerHandleRef.current = await App.addListener(
                "appUrlOpen",
                async (event: URLOpenListenerEvent) => {
                    const path = appLaunchUrlToPath(event.url);
                    console.log(path);
                    if (path && path.startsWith("/auth/verify-magic/")) {
                        const token = path.split("/auth/verify-magic/")[1];
                        if (token) {
                            await verifyMagicLinkOnce(token, { source: "native" });
                        }
                    }
                    if (!loggedInRef.current) {
                        const redirectDestination = path
                            ? path === "" || path === "/"
                                ? routes.tabResults
                                : path
                            : undefined;

                        if (history.location.pathname === routes.login) {
                            history.replace(routes.login, redirectDestination ? { redirectTo: redirectDestination } : undefined);
                        } else {
                            history.push(
                                routes.login,
                                redirectDestination
                                    ? { redirectTo: redirectDestination }
                                    : undefined
                            );
                        }
                        return;
                    }

                    // Check for Garmin deep link - used for iOS response to getKnownDevices
                    if (event.url.includes("device-select-resp")) {
                        // Dispatch an action to store Garmin params in Redux
                        processIosGarminDevices(event.url).catch((e) => {
                            console.error("Error processing Garmin devices", e);
                        });
                        return;
                    }

                    if (path) {
                        const utmParams = getUTMParamsFromURL(event.url);
                        mpRegisterSuperProperties(utmParams);
                        trackEvent("DeepLinkAppOpen", {
                            urlPath: path,
                            ...utmParams,
                        });

                        if (path === "" || path === "/") {
                            // the fallback route doesn't seem to work by default
                            history.push(routes.tabResults);
                        } else {
                            history.push(path);
                        }
                    }
                    // If no match, do nothing - let regular routing
                    // logic take over
                }
            );
        };

        registerListener();

        return () => {
            if (listenerHandleRef.current) {
                listenerHandleRef.current.remove();
                listenerHandleRef.current = null;
            }
        };
    }, [history]);

    const onExpiredMagicLinkAcknowledged = () => {
        setShowExpiredMagicLinkAlert(false);
        history.push(routes.login);
    };

    return (
        <AlertComponent
            testId="magic-link-expired-alert"
            showAlert={showExpiredMagicLinkAlert}
            title={t("login.login.magicLinkExpiredTitle")}
            description={t("login.login.magicLinkExpiredMessage")}
            buttonDone={t("help.OnboardingVideo.controls.ok")}
            dismissAlert={onExpiredMagicLinkAcknowledged}
            onDonePressed={onExpiredMagicLinkAcknowledged}
        />
    );
};

export default AppUrlListener;
